import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import { useAuth } from '../auth/AuthContext'
import { supabase } from '../supabaseClient'

// One-time profile confirmation, shown to a student whose record is missing
// anything we rely on.
//
// It began as a gender-only prompt. The fields around it turned out to be just
// as unreliable — of one five-student cohort, one had no city, one no state,
// one a school city typed in capitals — and those feed the fair a student is
// eligible for, the forms they need, and how a mentor addresses them. Asking
// once, with what we already know filled in, is cheaper than discovering a
// blank in February.
//
// Everything is prefilled, so this is a confirmation and not a form. It opens
// only when something is actually missing, and closes for good once saved.

// Matches profile/ and onboarding/: before August the upcoming fall is this
// calendar year, otherwise next.
const FALL_YEAR = new Date().getMonth() < 7
  ? new Date().getFullYear()
  : new Date().getFullYear() + 1

/* US states, DC and the territories a school can sit in. */
const US_STATES: [string, string][] = [
  ['AL','Alabama'],     ['AK','Alaska'],        ['AZ','Arizona'],       ['AR','Arkansas'],
  ['CA','California'],  ['CO','Colorado'],      ['CT','Connecticut'],   ['DE','Delaware'],
  ['DC','District of Columbia'],                ['FL','Florida'],       ['GA','Georgia'],
  ['HI','Hawaii'],      ['ID','Idaho'],         ['IL','Illinois'],      ['IN','Indiana'],
  ['IA','Iowa'],        ['KS','Kansas'],        ['KY','Kentucky'],      ['LA','Louisiana'],
  ['ME','Maine'],       ['MD','Maryland'],      ['MA','Massachusetts'], ['MI','Michigan'],
  ['MN','Minnesota'],   ['MS','Mississippi'],   ['MO','Missouri'],      ['MT','Montana'],
  ['NE','Nebraska'],    ['NV','Nevada'],        ['NH','New Hampshire'], ['NJ','New Jersey'],
  ['NM','New Mexico'],  ['NY','New York'],      ['NC','North Carolina'],['ND','North Dakota'],
  ['OH','Ohio'],        ['OK','Oklahoma'],      ['OR','Oregon'],        ['PA','Pennsylvania'],
  ['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],  ['TN','Tennessee'],
  ['TX','Texas'],       ['UT','Utah'],          ['VT','Vermont'],       ['VA','Virginia'],
  ['WA','Washington'],  ['WV','West Virginia'], ['WI','Wisconsin'],     ['WY','Wyoming'],
  ['PR','Puerto Rico'], ['GU','Guam'],          ['VI','U.S. Virgin Islands'],
]

/* school_state is a free-text VARCHAR(100), so legacy rows hold anything from
   "CA" to "California" to "california". Map what we can onto a code; anything
   unrecognised becomes blank so the student picks it rather than us guessing. */
function toStateCode(raw: string): string {
  const s = raw.trim()
  if (!s) return ''
  const up = s.toUpperCase()
  if (US_STATES.some(([code]) => code === up)) return up
  const byName = US_STATES.find(([, name]) => name.toUpperCase() === up)
  return byName ? byName[0] : ''
}

interface Draft {
  first_name: string
  last_name:  string
  grade:      string
  school:     string
  city:       string
  state:      string
  gender:     'male' | 'female' | ''
}

const EMPTY: Draft = {
  first_name: '', last_name: '', grade: '', school: '', city: '', state: '', gender: '',
}

export default function GenderPrompt() {
  const { session, role } = useAuth()
  const [open,   setOpen]   = useState(false)
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState<string | null>(null)
  const [d,      setD]      = useState<Draft>(EMPTY)

  useEffect(() => {
    let cancelled = false
    if (!session || role !== 'student') return
    ;(async () => {
      const [{ data: sp }, { data: u }] = await Promise.all([
        supabase.from('student_profile')
          .select('grade, school_name, school_city, school_state, gender')
          .eq('user_id', session.user.id).maybeSingle(),
        supabase.from('users')
          .select('first_name, last_name')
          .eq('user_id', session.user.id).maybeSingle(),
      ])
      if (cancelled || !sp) return

      const draft: Draft = {
        first_name: (u?.first_name ?? '').trim(),
        last_name:  (u?.last_name ?? '').trim(),
        grade:      sp.grade != null ? String(sp.grade) : '',
        school:     (sp.school_name ?? '').trim(),
        city:       (sp.school_city ?? '').trim(),
        state:      toStateCode(sp.school_state ?? ''),
        gender:     (sp.gender as Draft['gender']) ?? '',
      }
      setD(draft)
      /* Open only when something we need is actually blank. A complete record
         is never interrupted. */
      const incomplete = Object.values(draft).some(v => !v)
      if (incomplete) setOpen(true)
    })()
    return () => { cancelled = true }
  }, [session, role])

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD(p => ({ ...p, [k]: v }))
  const missing = Object.values(d).some(v => !String(v).trim())

  async function save() {
    if (!session || saving || missing) return
    /* grade is NOT NULL with CHECK (grade BETWEEN 6 AND 12); the dropdown only
       offers 6-12, so anything else means a corrupted draft, not a save. */
    const gradeNum = Number(d.grade)
    if (!Number.isInteger(gradeNum) || gradeNum < 6 || gradeNum > 12) {
      setError('Please choose your grade.')
      return
    }
    setSaving(true)
    setError(null)
    const [{ error: e1 }, { error: e2 }] = await Promise.all([
      supabase.from('users').update({
        first_name: d.first_name.trim(),
        last_name:  d.last_name.trim(),
      }).eq('user_id', session.user.id),
      supabase.from('student_profile').update({
        grade:        gradeNum,
        school_name:  d.school.trim(),
        school_city:  d.city.trim(),
        school_state: d.state,
        gender:       d.gender,
      }).eq('user_id', session.user.id),
    ])
    setSaving(false)
    if (e1 || e2) { setError((e1 ?? e2)!.message); return }
    setOpen(false)
  }

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Confirm your details</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2, fontSize: 14 }}>
          Your school and grade decide which science fair you can enter and which
          forms you need, so it is worth a moment now. Correct anything that is
          wrong.
        </DialogContentText>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="First name" size="small" required disabled={saving}
            value={d.first_name} onChange={e => set('first_name', e.target.value)}
          />
          <TextField
            label="Last name" size="small" required disabled={saving}
            value={d.last_name} onChange={e => set('last_name', e.target.value)}
          />
          <TextField
            label={`Grade in Fall ${FALL_YEAR}`} size="small" required disabled={saving}
            select value={d.grade} onChange={e => set('grade', e.target.value)}
          >
            {[6, 7, 8, 9, 10, 11, 12].map(g => (
              <MenuItem key={g} value={String(g)}>Grade {g}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="State" size="small" required disabled={saving}
            select value={d.state} onChange={e => set('state', e.target.value)}
          >
            {US_STATES.map(([code, name]) => (
              <MenuItem key={code} value={code}>{name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="School" size="small" required disabled={saving}
            sx={{ gridColumn: { sm: '1 / -1' } }}
            value={d.school} onChange={e => set('school', e.target.value)}
          />
          <TextField
            label="City" size="small" required disabled={saving}
            sx={{ gridColumn: { sm: '1 / -1' } }}
            value={d.city} onChange={e => set('city', e.target.value)}
          />
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 600, mt: 2.5, mb: 1 }}>
          So your mentor&rsquo;s feedback addresses you correctly
        </Typography>
        <ToggleButtonGroup
          exclusive size="small" value={d.gender} disabled={saving}
          onChange={(_, v) => { if (v) set('gender', v) }}
        >
          <ToggleButton value="male" sx={{ minWidth: 110 }}>Male</ToggleButton>
          <ToggleButton value="female" sx={{ minWidth: 110 }}>Female</ToggleButton>
        </ToggleButtonGroup>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" disabled={saving || missing} onClick={save}>
          {saving ? 'Saving…' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
